import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';

import { ToasterService } from '../../services/toaster.service';
import { AuthService } from '../../services/auth.service';
import { GlobalDialogService } from '../../services/dialog.service';
import { VoiceAssistantService } from '../../services/voice-assistant.service';
import { responseStatus } from '../constants/responseConst';
import { isTimestampDriftError } from '../helpers/hmac.helper';
import { NgxUiLoaderService } from 'ngx-ui-loader';

/**
 * Expected API response shape
 * (kept minimal on purpose)
 */
interface ApiResponse {
  statusCode?: number;
  message?: string;
}

const recentErrors = new Set<string>();
const errorTimeout = 5000;

export const getInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toaster = inject(ToasterService);
  const dialogWrapper = inject(GlobalDialogService);
  const authService = inject(AuthService);
  const voiceAssistant = inject(VoiceAssistantService);
  const ngxLoader = inject(NgxUiLoaderService);
  const platformId = inject(PLATFORM_ID);

  const isBrowser = isPlatformBrowser(platformId);
  const skipLoader = req.headers.get('X-Loader') === 'true';

  if (skipLoader && isBrowser) {
    ngxLoader.startLoader('mainLoader');
  }

  return next(req).pipe(
    tap({
      next: event => {
        if (!(event instanceof HttpResponse)) return;

        // 🔐 Angular 20 → HttpResponse<unknown>
        const body = event.body as ApiResponse | null;

        if (!body || typeof body.statusCode !== 'number') {
          return;
        }

        const skipSuccessToaster =
          req.headers.get('X-Skip-Success-Toaster') === 'true';

        switch (body.statusCode) {
          case responseStatus.SUCCESS:
            if (
              ['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method) &&
              body.message &&
              !skipSuccessToaster
            ) {
              toaster.showSuccess(body.message);
            }
            break;

          case responseStatus.BADREQUEST:
          case responseStatus.UNAUTHORIZED:
          case responseStatus.FORBIDDEN:
          case responseStatus.REQUESTTIMEOUT:
          case responseStatus.INTERNALSERVERERROR:
            toaster.showError(body.message ?? 'Something went wrong');

            if (
              body.statusCode === responseStatus.UNAUTHORIZED ||
              body.statusCode === responseStatus.FORBIDDEN
            ) {
              dialogWrapper.closeDialog();
            }
            break;
        }
      },
    }),

    catchError((error: HttpErrorResponse) => {
      // 🚨 SSR must never throw
      if (!isBrowser) {
        return EMPTY;
      }

      const errorMessage: string | undefined = error?.error?.message;

      if (errorMessage === 'SomethingWentWrong') {
        toaster.showError('Server Not Working');
        return EMPTY;
      }

      if (errorMessage && !recentErrors.has(errorMessage)) {
        recentErrors.add(errorMessage);
        toaster.showError(errorMessage);
        setTimeout(() => recentErrors.delete(errorMessage), errorTimeout);
      }

      if (isTimestampDriftError(error)) {
        return throwError(() => error);
      }

      if (
        error.status === responseStatus.UNAUTHORIZED ||
        error.status === responseStatus.FORBIDDEN
      ) {
        localStorage.clear();
        dialogWrapper.closeDialog();
        authService.profileDetails.set(null);
        voiceAssistant.disarm();
        localStorage.setItem('language', 'en');
        router.navigateByUrl('/auth/login');
      }

      return throwError(() => error);
    })
  );
};
